import { type JsonLd } from 'json-ld-types';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { clearMemoizedCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { type FragmentReaderInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { type FragmentWriterInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';

// This is the JsonLd type we expect from the onResolve function, originating from linking platform's content resolver service.
// This schema is unstable & subject to change, so this resolver validates it before consuming it further
export type ResolveResponseType = {
	data: JsonLd.Data.Document;
	meta: JsonLd.Meta.BaseMeta;
};

const getContentIdFromResolvedData = (resolvedData: ResolveResponseType): string | undefined => {
	const ari = resolvedData.data['atlassian:ari'];
	return ari?.substring?.(ari?.lastIndexOf?.('/') + 1);
};

// data.data['@type'] will have two elements, for example ['schema:DigitalDocument', 'Document'], where 'schema:DigitalDocument' specifically denotes a live doc
// The resolve logic doesn't consistently position these two elements in the @type array, so the schema we need to validate may be in the 0th or 1st element
const isResolvedDataUnarchivedLiveDoc = (resolvedData: ResolveResponseType): boolean => {
	const typeArr = resolvedData.data['@type'];
	const isLiveDoc =
		typeArr?.[0] === 'schema:DigitalDocument' || typeArr?.[1] === 'schema:DigitalDocument';
	const isArchived = resolvedData.data['atlassian:state'] === 'archived';
	return isLiveDoc && !isArchived;
};

const isResolvedDataValid = (resolvedData: unknown): resolvedData is ResolveResponseType => {
	if (!resolvedData || typeof resolvedData !== 'object') return false;

	const data = (resolvedData as any).data;
	const meta = (resolvedData as any).meta;
	if (!data || !meta) return false;

	if (!('@context' in data)) return false;

	const type = data['@type'];
	if (typeof type !== 'string' && !Array.isArray(type)) return false;

	return true;
};

let hasInvalidResolverDataAnalyticsEventTriggered = false;
export const liveDocSmartCardResolver = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	readFragmentInterop: FragmentReaderInterop<any>,
	writeFragmentInterop: FragmentWriterInterop<any>,
	resolvedData?: unknown,
): void => {
	if (isResolvedDataValid(resolvedData)) {
		if (isResolvedDataUnarchivedLiveDoc(resolvedData)) {
			const contentId = getContentIdFromResolvedData(resolvedData);
			if (contentId) {
				let existingCachedData:
					| { subType: string; status: string; __typename: string }
					| null
					| undefined;
				try {
					existingCachedData = readFragmentInterop({
						id: `Content:${contentId}`,
					});
				} catch {
					// readFragment throws an error if no data is found
				}

				// Reads are cheaper than writes, so only write if we don't already have the correct data in cache.
				if (
					!existingCachedData ||
					existingCachedData?.subType !== 'live' ||
					existingCachedData?.status !== 'current'
				) {
					writeFragmentInterop({
						fragmentId: `Content:${contentId}`,
						// status is current if we are an unarchived live doc
						data: { __typename: 'Content', id: contentId, subType: 'live', status: 'current' },
					});
					// Since graphql cache has been updated, make sure the memoization cache for getCachedPageData is cleared
					// So if getCachedPageData is called again with the same content id, it will read the updated graphql cache instead of returning the memoized data
					clearMemoizedCachedPageData();
				}
			}
		}
	} else {
		// If resolved data schema is invalid, we likely need to update it ASAP as Linking Platform's content resolver service API may have changed
		if (!hasInvalidResolverDataAnalyticsEventTriggered) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'invalidResolverData',
					actionSubject: 'liveDocSmartCardResolver',
					source: 'confluenceSmartCardProvider',
				},
			}).fire();

			// Only send this event once per load to reduce load on analytics pipeline
			hasInvalidResolverDataAnalyticsEventTriggered = true;
		}
	}
};
